import { CheckOutlined, SearchOutlined } from '@ant-design/icons';
import { Button, Input, Modal, Select, Typography, theme } from 'antd';
import type { BaseSelectRef } from 'rc-select';
import { forwardRef, useMemo, useState, type CSSProperties, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

export interface ModalSelectOption {
  value: string;
  label: string;
  /** Secondary line under the label in the modal list (also searchable). */
  description?: string;
  disabled?: boolean;
}

interface ModalSelectProps {
  value?: string | null;
  /** Form-controlled path: Ant Form injects value/onChange. */
  onChange?: (value: string | undefined) => void;
  /** Imperative pick that must not persist a value (ExtraSkladItems "add"). */
  onSelect?: (value: string) => void;
  /** Distinct clear affordance — e.g. MoyskladTab clears to null via mutation. */
  onClear?: () => void;
  options: ModalSelectOption[];
  placeholder?: string;
  /** Modal heading; falls back to the placeholder. */
  title?: string;
  loading?: boolean;
  allowClear?: boolean;
  disabled?: boolean;
  size?: 'small' | 'middle' | 'large';
  variant?: 'outlined' | 'filled' | 'borderless';
  style?: CSSProperties;
  suffixIcon?: ReactNode;
}

/**
 * A Select replacement that opens the options in a Modal instead of a
 * body-mounted dropdown. Since the desktop shell scrolls inside its own
 * Content box, a native dropdown detaches and the page scrolls under it; a
 * modal keeps the background inert (mask) and confines scrolling to the
 * options list. The trigger is a display-only Ant `Select` (never opens its
 * own dropdown) so the field looks identical to the rest of the app.
 *
 * Single-select, scalar value, label-substring search — the only shape any of
 * the app's Selects actually use.
 */
export const ModalSelect = forwardRef<BaseSelectRef, ModalSelectProps>(function ModalSelect(
  {
    value,
    onChange,
    onSelect,
    onClear,
    options,
    placeholder,
    title,
    loading,
    allowClear,
    disabled,
    size = 'large',
    variant,
    style,
    suffixIcon,
  },
  ref,
) {
  const { t } = useTranslation();
  const { token } = theme.useToken();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return options;
    return options.filter(
      (o) => o.label.toLowerCase().includes(q) || (o.description ?? '').toLowerCase().includes(q),
    );
  }, [options, query]);

  const openModal = (): void => {
    if (disabled) return;
    setQuery('');
    setOpen(true);
  };
  const close = (): void => setOpen(false);

  const pick = (v: string): void => {
    if (onSelect) onSelect(v);
    else onChange?.(v);
    close();
  };
  const clear = (): void => {
    if (onClear) onClear();
    else onChange?.(undefined);
    close();
  };

  return (
    <>
      <Select
        ref={ref}
        open={false}
        showSearch={false}
        allowClear={false}
        value={value ?? undefined}
        options={options}
        placeholder={placeholder}
        size={size}
        variant={variant}
        style={style}
        loading={loading}
        disabled={disabled}
        suffixIcon={suffixIcon}
        onClick={openModal}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            openModal();
          }
        }}
      />
      <Modal
        open={open}
        onCancel={close}
        title={title ?? placeholder ?? t('common.select')}
        footer={null}
        destroyOnClose
        // Sit above any Drawer/Modal this picker was opened from (both 1000).
        zIndex={1100}
      >
        <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
          <Input
            prefix={<SearchOutlined />}
            placeholder={t('common.search')}
            allowClear
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            size={size}
          />
          {allowClear ? (
            <Button size={size} onClick={clear}>
              {t('common.clear')}
            </Button>
          ) : null}
        </div>
        <div
          style={{
            maxHeight: '60vh',
            overflowY: 'auto',
            overscrollBehavior: 'contain',
            WebkitOverflowScrolling: 'touch',
          }}
        >
          {filtered.length === 0 ? (
            <div style={{ padding: '24px 4px', textAlign: 'center' }}>
              <Typography.Text type="secondary">{t('common.notFound')}</Typography.Text>
            </div>
          ) : (
            filtered.map((o) => {
              const selected = o.value === value;
              return (
                <div
                  key={o.value}
                  onClick={() => {
                    if (!o.disabled) pick(o.value);
                  }}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 12,
                    padding: '12px 4px',
                    borderTop: `1px solid ${token.colorBorderSecondary}`,
                    cursor: o.disabled ? 'not-allowed' : 'pointer',
                    color: o.disabled
                      ? token.colorTextDisabled
                      : selected
                        ? token.colorPrimary
                        : token.colorText,
                    fontWeight: selected ? 600 : 400,
                  }}
                >
                  <span style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <span>{o.label}</span>
                    {o.description ? (
                      <Typography.Text type="secondary" style={{ fontSize: 12, fontWeight: 400 }}>
                        {o.description}
                      </Typography.Text>
                    ) : null}
                  </span>
                  {selected ? <CheckOutlined /> : null}
                </div>
              );
            })
          )}
        </div>
      </Modal>
    </>
  );
});
